import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { useGetLeaderboardQuery } from 'store/services/leaderboard/leaderboardApi';
import {
  LeaderboardCategory,
  LeaderboardPeriod,
  LeaderboardEntry,
} from 'types/interfaces/leaderboard';
import LeaderboardPodium from './components/LeaderboardPodium';
import LeaderboardTabs from './components/LeaderboardTabs';
import LeaderboardFilters from './components/LeaderboardFilters';
import LeaderboardTable from './components/LeaderboardTable';
import YourRankCallout from './components/YourRankCallout';
import './leaderboard.css';

const LeaderboardPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // URL-based state persistence for tab/filter navigation
  const [searchParams, setSearchParams] = useSearchParams();

  // Leaderboard state - initialized from URL params, then sessionStorage fallback
  const [category, setCategory] = useState<LeaderboardCategory>(
    () => (searchParams.get('tab') as LeaderboardCategory)
      || (sessionStorage.getItem('leaderboard_tab') as LeaderboardCategory)
      || 'most_trades'
  );
  const [period, setPeriod] = useState<LeaderboardPeriod>(
    () => (searchParams.get('period') as LeaderboardPeriod)
      || (sessionStorage.getItem('leaderboard_period') as LeaderboardPeriod)
      || 'all_time'
  );
  const [country, setCountry] = useState<string>(
    () => searchParams.get('country') || sessionStorage.getItem('leaderboard_country') || ''
  );
  const [search, setSearch] = useState<string>(
    () => searchParams.get('search') || sessionStorage.getItem('leaderboard_search') || ''
  );
  const [page, setPage] = useState<number>(1);

  // Store all loaded rankings for pagination
  const [allRankings, setAllRankings] = useState<LeaderboardEntry[]>([]);

  // Fetch leaderboard data
  const { data, isLoading, isFetching } = useGetLeaderboardQuery({
    category,
    period,
    country: country || undefined,
    search: search || undefined,
    page,
    limit: 25,
    // Filter out users with empty want lists on Most Points tab
    minWantListCount: category === 'most_points' ? 1 : undefined,
  });

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Sync filter state to URL params and sessionStorage
  useEffect(() => {
    const params: Record<string, string> = {};
    if (category !== 'most_trades') params.tab = category;
    if (period !== 'all_time') params.period = period;
    if (country) params.country = country;
    if (search) params.search = search;
    setSearchParams(params, { replace: true });

    // Persist to sessionStorage so sidebar navigation restores state
    sessionStorage.setItem('leaderboard_tab', category);
    sessionStorage.setItem('leaderboard_period', period);
    sessionStorage.setItem('leaderboard_country', country);
    sessionStorage.setItem('leaderboard_search', search);
  }, [category, period, country, search, setSearchParams]);

  // Reset pagination and rankings when filters change
  useEffect(() => {
    setPage(1);
    setAllRankings([]);
  }, [category, period, country, search]);

  // Update allRankings when new data arrives
  useEffect(() => {
    if (data) {
      if (page === 1) {
        setAllRankings(data.rankings);
      } else {
        setAllRankings((prev) => [...prev, ...data.rankings]);
      }
    }
  }, [data, page]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (data?.hasMore && !isFetching) {
      setPage((prev) => prev + 1);
    }
  }, [data?.hasMore, isFetching]);

  const handleSearchChange = useCallback((newSearch: string) => {
    setSearch(newSearch);
  }, []);

  const handleCountryChange = useCallback((newCountry: string) => {
    setCountry(newCountry);
  }, []);

  const handlePeriodChange = useCallback((newPeriod: LeaderboardPeriod) => {
    setPeriod(newPeriod);
  }, []);

  const handleCategoryChange = useCallback((newCategory: LeaderboardCategory) => {
    setCategory(newCategory);
  }, []);

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHelmet
        title="Leaderboard - Top Traders | TradeMagic"
        description="Discover the most active Magic: The Gathering traders on TradeMagic. View rankings, ratings, and trade history for our top community members."
        canonical="https://trademagic.gg/leaderboard"
        keywords="MTG trading leaderboard, Magic card traders, TradeMagic rankings, top traders, Magic The Gathering trading"
        type="website"
      />

      {/* BreadcrumbList Schema */}
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Leaderboard', url: 'https://trademagic.gg/leaderboard' },
        ]}
      />

      <div className="leaderboard-layout">
        {/* Mobile Menu Overlay */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="leaderboard-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="leaderboard-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/leaderboard', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="leaderboard-main">
            {/* Top Navigation Bar */}
            <TopNavigationBar
              title="Leaderboard"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="leaderboard-container">
              {/* Page Header */}
              <header className="leaderboard-header">
                <h1 className="leaderboard-title">Leaderboard</h1>
                <p className="leaderboard-subtitle">
                  The most active traders in the TradeMagic community
                </p>
              </header>

              {/* Podium (Top 3) - Only show on page 1 */}
              {page === 1 && (
                <LeaderboardPodium
                  podium={data?.podium || []}
                  category={category}
                  isLoading={isLoading}
                />
              )}

              {/* Category Tabs */}
              <LeaderboardTabs
                activeCategory={category}
                onCategoryChange={handleCategoryChange}
              />

              {/* Filters */}
              <div className="leaderboard-filters-section">
                <LeaderboardFilters
                  activePeriod={period}
                  onPeriodChange={handlePeriodChange}
                  country={country}
                  onCountryChange={handleCountryChange}
                  search={search}
                  onSearchChange={handleSearchChange}
                />
              </div>

              {/* Your Rank Callout */}
              <YourRankCallout
                currentUserRank={data?.currentUser || null}
                category={category}
                isLoggedIn={!!currentUser}
              />

              {/* Rankings Table (#4+) */}
              <LeaderboardTable
                rankings={allRankings}
                category={category}
                isLoading={isLoading && page === 1}
                currentUserId={currentUser?.id}
                onLoadMore={handleLoadMore}
                hasMore={data?.hasMore}
                loadingMore={isFetching && page > 1}
              />

              {/* Result count */}
              {data && !isLoading && (
                <div className="leaderboard-result-count">
                  Showing {(data.podium?.length || 0) + allRankings.length} of {data.totalCount} traders
                  {country && ` from ${country}`}
                  {search && ` matching "${search}"`}
                </div>
              )}
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default LeaderboardPage;
